import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Briefcase,
    Calendar,
    User,
    Users,
    MapPin,
    Info,
    Building2,
    Clock,
    Target,
    Settings,
    Phone,
    Mail,
    UserCheck,
    Hash,
    DollarSign,
    CheckCircle2,
    AlertCircle,
    XCircle,
    Activity,
    ArrowLeft,
    Edit,
    Loader2,
    Package,
    UserCog,
    ClipboardCheck,
    Zap,
} from "lucide-react";
import PageBreadcrumb from "../../../../shared/PageBreadCrumb";
import RightDrawer from "../../../../shared/components/form/RightDrawer";
import AmsProjectAssetManagement from "../../components/AmsProjectAssetManagement";
import ProjectProgressHeader from "../../../project/components/ProjectProgressHeader";
import { isProjectInfoComplete, getProjectStatusInfo, STATUS_STYLE_MAP } from "../../../project/utils/projectStatusUtils";
import { safeFormatDate, safeFormatDistance } from "../../../../utils/dateUtils";
import amsProjectService from "../../../../services/amsProjectService";
import { toast } from "react-toastify";

function getStatusConfig(status: string) {
    const normalizedStatus = status?.toLowerCase() || "unknown";
    switch (normalizedStatus) {
        case "active":
        case "operational":
            return {
                bg: "bg-emerald-50",
                text: "text-emerald-700",
                border: "border-emerald-200",
                icon: CheckCircle2,
            };
        case "completed":
            return {
                bg: "bg-blue-50",
                text: "text-blue-700",
                border: "border-blue-200",
                icon: CheckCircle2,
            };
        case "pending":
        case "planning":
            return {
                bg: "bg-amber-50",
                text: "text-amber-700",
                border: "border-amber-200",
                icon: Clock,
            };
        case "on-hold":
            return {
                bg: "bg-orange-50",
                text: "text-orange-700",
                border: "border-orange-200",
                icon: AlertCircle,
            };
        case "cancelled":
            return {
                bg: "bg-red-50",
                text: "text-red-700",
                border: "border-red-200",
                icon: XCircle,
            };
        default:
            return {
                bg: "bg-slate-50",
                text: "text-slate-700",
                border: "border-slate-200",
                icon: Activity,
            };
    }
}

function getLocationString(project: any): string {
    if (
        project.locationId &&
        typeof project.locationId === "object" &&
        project.locationId.name
    ) {
        return project.locationId.name;
    }
    if (project.location && project.location !== "undefined") {
        return project.location;
    }
    if (project.city && project.city !== "undefined") {
        return project.city;
    }
    return "Not specified";
}

function InfoRow({
    icon: Icon,
    label,
    value,
    className = "",
    valueClassName = "",
    subValue,
}: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    value: string | number | null | undefined;
    className?: string;
    valueClassName?: string;
    subValue?: string;
}) {
    if (value == null || value === "" || value === "undefined") return null;
    return (
        <div
            className={`flex items-center gap-2 py-1.5 px-2 rounded border-b border-slate-50 last:border-b-0 ${className}`}
        >
            <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[11px] text-slate-500 flex-shrink-0">{label}</span>
                    <span
                        className={`text-xs font-medium text-slate-800 text-right break-words ${valueClassName}`}
                    >
                        {String(value)}
                    </span>
                </div>
                {subValue && (
                    <span className="text-[10px] text-slate-400 text-right block">
                        {subValue}
                    </span>
                )}
            </div>
        </div>
    );
}

function SectionCard({
    title,
    icon: Icon,
    children,
    delay = 0,
}: {
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    children: React.ReactNode;
    delay?: number;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay, duration: 0.2 }}
            className="bg-white rounded-lg border border-slate-200 overflow-hidden"
        >
            <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/80 flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5 text-teal-600" />
                <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    {title}
                </h3>
            </div>
            <div className="p-1.5">{children}</div>
        </motion.div>
    );
}

export default function ViewAmsProject() {
    const navigate = useNavigate();
    const { projectId } = useParams<{ projectId: string }>();
    const [project, setProject] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [openEquipmentDrawer, setOpenEquipmentDrawer] = useState(false);
    const [progressRefreshKey, setProgressRefreshKey] = useState(0);

    useEffect(() => {
        if (projectId) {
            loadProject();
        }
    }, [projectId]);

    const loadProject = async () => {
        if (!projectId) return;
        try {
            setLoading(true);
            const data = await amsProjectService.getProjectById(projectId);
            setProject(data);
        } catch (error: unknown) {
            const err = error as { message?: string };
            toast.error(err?.message || "Failed to load project");
            navigate("/projects");
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => navigate("/projects");
    const handleEdit = () =>
        navigate(`/projects/edit/${project?._id || project?.id}`);
    const handleOpenEquipment = () => {
        if (!isProjectInfoComplete(project)) {
            navigate(`/projects/edit/${project?._id || project?.id}?completeSetup=1`);
            return;
        }
        setOpenEquipmentDrawer(true);
    };
    const handleAssignments = () =>
        navigate(`/ams-projects/${project?._id || project?.id}/assignments`);
    const handleManageResources = () =>
        navigate(`/ams-projects/${project?._id || project?.id}/manage-resources`);
    const handleCalendar = () =>
        navigate(`/ams-projects/${project?._id || project?.id}/calendar`);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-teal-600" />
                    <p className="text-slate-600 font-medium">Loading project…</p>
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-12">
                <div className="text-center bg-white rounded-xl border border-slate-200 p-12">
                    <Briefcase className="w-14 h-14 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Project not found</p>
                    <button
                        onClick={handleBack}
                        className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                    >
                        Back to Projects
                    </button>
                </div>
            </div>
        );
    }

    const statusConfig = getStatusConfig(
        project.projectStatus || project.status
    );
    const StatusIcon = statusConfig.icon;
    const locationDisplay = getLocationString(project);

    return (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 pb-6">
            <PageBreadcrumb
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Projects", path: "/projects" },
                    {
                        name: project.projectName || project.name || "View",
                        path: undefined,
                    },
                ]}
            />

            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2 min-w-0">
                    <button
                        onClick={handleBack}
                        className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors flex-shrink-0"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Back
                    </button>
                    <div className="h-4 w-px bg-slate-200" />
                    <div className="min-w-0">
                        <h1 className="text-lg sm:text-xl font-bold text-slate-900 truncate">
                            {project.projectName || project.name}
                        </h1>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500">
                            <span className="font-mono">
                                {project.projectId || project.code || "N/A"}
                            </span>
                            <span>•</span>
                            <span>{project.projectCategory || "General"}</span>
                            {locationDisplay !== "Not specified" && (
                                <>
                                    <span>•</span>
                                    <span className="flex items-center gap-0.5">
                                        <MapPin className="w-3 h-3" />
                                        {locationDisplay}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <div
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-semibold ${statusConfig.bg} ${statusConfig.border} ${statusConfig.text}`}
                    >
                        <StatusIcon className="w-3.5 h-3.5" />
                        {project.projectStatus || project.status || "Unknown"}
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mr-1">Actions</span>
                <button
                    onClick={handleEdit}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md transition-colors"
                >
                    <Edit className="w-3.5 h-3.5" />
                    Edit AMS Project
                </button>
                <button
                    onClick={handleOpenEquipment}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-md transition-colors"
                >
                    <Package className="w-3.5 h-3.5" />
                    Equipment & Assets
                </button>
                <button
                    onClick={handleAssignments}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-md transition-colors"
                >
                    <Users className="w-3.5 h-3.5" />
                    Assignments
                </button>
                <button
                    onClick={handleManageResources}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-md transition-colors"
                >
                    <UserCog className="w-3.5 h-3.5" />
                    Manage Resources
                </button>
                <button
                    onClick={handleCalendar}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-md transition-colors"
                >
                    <Calendar className="w-3.5 h-3.5" />
                    Calendar
                </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                {typeof project.progress === "number" && (
                    <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2 min-w-[120px]">
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                                Progress
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className="bg-teal-600 h-full rounded-full"
                                        style={{ width: `${project.progress}%` }}
                                    />
                                </div>
                                <span className="text-xs font-bold text-teal-700 flex-shrink-0">
                                    {project.progress}%
                                </span>
                            </div>
                        </div>
                    </div>
                )}
                {project.startDate && (
                    <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 min-w-[100px]">
                        <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                            Start
                        </div>
                        <div className="text-xs font-medium text-slate-800">
                            {safeFormatDate(project.startDate, "MMM dd, yyyy")}
                        </div>
                    </div>
                )}
                {project.endDate && (
                    <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 min-w-[100px]">
                        <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                            End
                        </div>
                        <div className="text-xs font-medium text-slate-800">
                            {safeFormatDate(project.endDate, "MMM dd, yyyy")}
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <SectionCard title="Approval Status" icon={ClipboardCheck} delay={0}>
                    {(() => {
                        const statusInfo = getProjectStatusInfo(project);
                        const StatusBadgeIcon =
                            statusInfo.tone === 'rejected' ? XCircle :
                            statusInfo.tone === 'approved' || statusInfo.tone === 'started' ? CheckCircle2 :
                            statusInfo.tone === 'review' ? Clock :
                            AlertCircle;
                        return (
                            <div className="p-1.5 space-y-2">
                                <div className={`flex items-start gap-2 px-2.5 py-2 rounded-md border ${STATUS_STYLE_MAP[statusInfo.tone]}`}>
                                    <StatusBadgeIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <div className="text-xs font-semibold">{statusInfo.label}</div>
                                        {statusInfo.description && (
                                            <div className="text-[11px] opacity-80 mt-0.5">{statusInfo.description}</div>
                                        )}
                                    </div>
                                </div>
                                {statusInfo.tone === 'rejected' && project.rejectionReason && (
                                    <div className="px-2.5 py-2 bg-red-50 border border-red-100 rounded-md text-[11px] text-red-800">
                                        <span className="font-semibold">Reason: </span>
                                        {project.rejectionReason}
                                    </div>
                                )}
                                <button
                                    onClick={handleOpenEquipment}
                                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-md transition-colors"
                                >
                                    <Package className="w-3.5 h-3.5" />
                                    View audits & resolve
                                </button>
                            </div>
                        );
                    })()}
                </SectionCard>

                <SectionCard title="Project Overview" icon={Info} delay={0.05}>
                    <div className="space-y-0">
                        <InfoRow
                            icon={Target}
                            label="Type"
                            value={
                                project.projectTypeId?.name ||
                                project.projectType ||
                                project.category
                            }
                        />
                        <InfoRow
                            icon={Settings}
                            label="Priority"
                            value={project.priority}
                            valueClassName={
                                project.priority === "High"
                                    ? "text-red-600"
                                    : project.priority === "Medium"
                                        ? "text-amber-600"
                                        : "text-emerald-600"
                            }
                        />
                        <InfoRow icon={ClipboardCheck} label="Requirement" value={project.requirement} />
                        {project.acCapacityMw != null && <InfoRow icon={Zap} label="AC Capacity" value={`${project.acCapacityMw} MW AC`} />}
                        {project.dcCapacityMw != null && <InfoRow icon={Zap} label="DC Capacity" value={`${project.dcCapacityMw} MW DC`} />}
                        {project.description && (
                            <div className="p-2 mt-1 bg-slate-50 rounded border border-slate-100">
                                <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-0.5">
                                    Description
                                </div>
                                <p className="text-xs text-slate-700 leading-snug line-clamp-4">
                                    {project.description}
                                </p>
                            </div>
                        )}
                    </div>
                </SectionCard>

                <SectionCard title="Location Details" icon={MapPin} delay={0.08}>
                    <div className="space-y-0">
                        <InfoRow icon={MapPin} label="Site" value={locationDisplay} />
                        <InfoRow icon={MapPin} label="State" value={project.state} />
                        <InfoRow icon={MapPin} label="Country" value={project.country} />
                        <InfoRow icon={MapPin} label="Address" value={project.address} />
                        {(project.latitude || project.longitude) && (
                            <InfoRow
                                icon={Target}
                                label="Coordinates"
                                value={`${project.latitude ?? "-"}, ${project.longitude ?? "-"}`}
                            />
                        )}
                    </div>
                </SectionCard>

                <SectionCard title="Customer" icon={Building2} delay={0.1}>
                    <div className="space-y-0">
                        <InfoRow
                            icon={Building2}
                            label="Company"
                            value={
                                project.customerId?.companyName ||
                                project.customer?.companyName
                            }
                            valueClassName="font-semibold text-slate-900"
                        />
                        <InfoRow
                            icon={User}
                            label="Contact Person"
                            value={project.customerId?.name || project.customer?.name}
                        />
                        <InfoRow
                            icon={Mail}
                            label="Email"
                            value={project.customerId?.email || project.customer?.email}
                            valueClassName="text-blue-600 hover:underline cursor-pointer"
                        />
                        <InfoRow
                            icon={Phone}
                            label="Phone"
                            value={project.customerId?.phone || project.customer?.phone}
                        />
                    </div>
                </SectionCard>

                <SectionCard title="Project Team" icon={Users} delay={0.12}>
                    <div className="space-y-0">
                        <InfoRow
                            icon={User}
                            label="Project Manager"
                            value={
                                project.assignedResources?.projectManager?.name ||
                                project.manager?.name ||
                                project.projectManager ||
                                project.manager
                            }
                            subValue={project.assignedResources?.projectManager?.email}
                            valueClassName="font-semibold text-slate-900"
                        />
                        <InfoRow
                            icon={UserCheck}
                            label="Project Head"
                            value={
                                project.assignedResources?.projectHead?.name ||
                                project.projectHead ||
                                project.projectHeadName
                            }
                            subValue={project.assignedResources?.projectHead?.email}
                            valueClassName="font-semibold text-slate-900"
                        />

                        {project.assignedResources?.auditors &&
                            project.assignedResources.auditors.length > 0 && (
                                <div className="pt-2 mt-1 border-t border-slate-100">
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                        <UserCheck className="w-3 h-3 text-emerald-500" />
                                        <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                                            Auditors
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {project.assignedResources.auditors.map(
                                            (auditor: { name?: string }, index: number) => (
                                                <span
                                                    key={index}
                                                    className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-[11px] font-medium border border-emerald-100"
                                                >
                                                    <span className="w-4 h-4 rounded-full bg-emerald-200 flex items-center justify-center text-[9px] font-bold flex-shrink-0">
                                                        {auditor.name?.charAt(0) || "A"}
                                                    </span>
                                                    {auditor.name}
                                                </span>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}
                    </div>
                </SectionCard>

                <SectionCard title="Timeline" icon={Calendar} delay={0.15}>
                    <div className="space-y-0">
                        <InfoRow
                            icon={Calendar}
                            label="Start Date"
                            value={
                                project.startDate
                                    ? safeFormatDate(project.startDate, "MMM dd, yyyy")
                                    : null
                            }
                            subValue={
                                project.startDate
                                    ? safeFormatDistance(project.startDate, { addSuffix: true })
                                    : undefined
                            }
                        />
                        <InfoRow
                            icon={Calendar}
                            label="End Date"
                            value={
                                project.endDate
                                    ? safeFormatDate(project.endDate, "MMM dd, yyyy")
                                    : null
                            }
                            subValue={
                                project.endDate
                                    ? safeFormatDistance(project.endDate, { addSuffix: true })
                                    : undefined
                            }
                        />
                        <InfoRow
                            icon={Clock}
                            label="Duration"
                            value={
                                project.projectDurationMonths
                                    ? `${project.projectDurationMonths} months`
                                    : null
                            }
                        />
                        <InfoRow
                            icon={Target}
                            label="Est. Completion"
                            value={
                                project.estimatedCompletionDate
                                    ? safeFormatDate(
                                        project.estimatedCompletionDate,
                                        "MMM dd, yyyy"
                                    )
                                    : null
                            }
                        />
                        <InfoRow
                            icon={CheckCircle2}
                            label="Actual Completion"
                            value={
                                project.actualCompletionDate
                                    ? safeFormatDate(
                                        project.actualCompletionDate,
                                        "MMM dd, yyyy"
                                    )
                                    : null
                            }
                            valueClassName="text-emerald-600 font-medium"
                        />
                    </div>
                </SectionCard>

                <SectionCard title="Additional" icon={Hash} delay={0.2}>
                    <div className="p-1.5">
                        {project.tags && project.tags.length > 0 && (
                            <div className="mb-2">
                                <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                                    Tags
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {project.tags.map((tag: string, index: number) => (
                                        <span
                                            key={index}
                                            className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(project.dateCreated || project.createdAt) && (
                            <div className="flex items-center gap-1.5 text-[10px] text-slate-500 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                <Clock className="w-3 h-3" />
                                <span>
                                    Created {safeFormatDate(project.dateCreated || project.createdAt, "MMM dd, yyyy")}
                                    {project.createdBy && typeof project.createdBy === "object" && project.createdBy.name && (
                                        <> by {project.createdBy.name}</>
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </SectionCard>
            </div>

            <div className="mt-4 flex items-center justify-between text-[10px] text-slate-500 border-t border-slate-100 pt-3">
                <span>Manage assets & resources from project list</span>
                <button onClick={handleBack} className="text-teal-600 hover:text-teal-700 font-medium">
                    ← Back to projects
                </button>
            </div>

            <RightDrawer
                open={openEquipmentDrawer}
                title={project ? `${project.projectName || project.name} - Equipment & Assets` : "Equipment & Assets"}
                onClose={() => setOpenEquipmentDrawer(false)}
                size="full"
            >
                <div className="p-0">
                    {project && (
                        <>
                            <div className="border-b border-slate-200">
                                <ProjectProgressHeader
                                    project={project}
                                    refreshSignal={progressRefreshKey}
                                    isAms={true}
                                    onRefresh={() => {
                                        loadProject();
                                        setProgressRefreshKey((k) => k + 1);
                                    }}
                                />
                            </div>
                            <div className="p-6">
                                <AmsProjectAssetManagement
                                    project={project}
                                    onEquipmentUpdate={() => {
                                        loadProject();
                                        setProgressRefreshKey((k) => k + 1);
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>
            </RightDrawer>
        </div>
    );
}
